import {
  AdServer,
  AfsPlacementId,
  DisplayPlacementId,
  FinnLegendaryAfsPlacementId,
  FinnLegendaryDisplayPlacementId,
  type PlacementId
} from './api/index.js';

export type ContainerId<
  TPlacementId extends PlacementId = PlacementId,
  TAdServer extends AdServer = AdServer
> = TAdServer extends AdServer.Afs
  ? `${TPlacementId}`
  : `${TPlacementId}--container`;

export namespace ContainerId {
  export function fromPlacementId<
    TPlacementId extends PlacementId,
    TAdServer extends AdServer
  >(
    placementId: TPlacementId,
    adServer?: TAdServer
  ): ContainerId<TPlacementId, TAdServer> {
    return (
      adServer === AdServer.Afs ? placementId : `${placementId}--container`
    ) as ContainerId<TPlacementId, TAdServer>;
  }
}

export type AdSlot<T extends PlacementId = PlacementId> = `${T}--slot`;

export namespace AdSlot {
  export function fromPlacementId<T extends PlacementId>(
    placementId: T
  ): AdSlot<T> {
    return `${placementId}--slot`;
  }
}

const placementIdEnums = [
  AfsPlacementId,
  DisplayPlacementId,
  FinnLegendaryAfsPlacementId,
  FinnLegendaryDisplayPlacementId
] as const;

type PlacementIdEnumType = (typeof placementIdEnums)[number];

type PlacementIdEntry<TPlacementId extends PlacementIdEnumType> = {
  [TKey in keyof TPlacementId]: [TKey, TPlacementId[TKey]];
}[keyof TPlacementId];

type CombinedPlacementEntry<
  TPlacementIds extends ReadonlyArray<PlacementIdEnumType>
> = {
  [TKey in keyof TPlacementIds]: PlacementIdEntry<TPlacementIds[TKey]>;
}[number];

const adPlacementEntries = placementIdEnums
  .flatMap<CombinedPlacementEntry<typeof placementIdEnums>>(
    (placementIdEnum) =>
      Object.entries(placementIdEnum) as Array<
        PlacementIdEntry<typeof placementIdEnum>
      >
  )
  .map(
    ([enumKey, placementIdValue]) =>
      [
        enumKey,
        {
          id: placementIdValue,
          containerId: ContainerId.fromPlacementId(placementIdValue),
          slot: AdSlot.fromPlacementId(placementIdValue)
        }
      ] as AdPlacement.Entry<typeof placementIdEnums>
  );

type AdPlacementType = {
  [TKey in (typeof adPlacementEntries)[number] as TKey[0]]: TKey[1];
};

export type GenericValue<
  TPlacementId extends PlacementId = PlacementId,
  TAdServer extends AdServer = AdServer
> = {
  id: TPlacementId;
  containerId: ContainerId<TPlacementId, TAdServer>;
  slot: AdSlot<TPlacementId>;
};

export namespace AdPlacement {
  export type Value = AdPlacementType[keyof AdPlacementType];
  export type Entry<TPlacementIds extends ReadonlyArray<PlacementIdEnumType>> =
    {
      [TKey in keyof TPlacementIds]: {
        [TKey2 in keyof TPlacementIds[TKey]]: TPlacementIds[TKey][TKey2] extends PlacementId
          ? [TKey2, GenericValue<TPlacementIds[TKey][TKey2]>]
          : never;
      }[keyof TPlacementIds[TKey]];
    }[number];
}

export const AdPlacement = Object.fromEntries(
  adPlacementEntries
) as AdPlacementType;
